<template>
  <div class="student-profile navigation-top-app">
    <navigation-top-app
      :app="app"
      :back="backAction"
    >
      <template v-slot:center>
        <div id="navigation-center">
          {{ app.user.role === 'student' ? app.strings.app.student.profile.title_own : app.strings.app.student.profile.title }}
        </div>
      </template>
    </navigation-top-app>
    <transition name="fade">
      <div id="student-profile-content" class="max-width-800 margin-auto" v-if="show && !loading && student.data.config">
        <div class="student-data">
          <div class="student-head">
            <img src="@/assets/images/avatar.svg" />
            <div class="student-identity">
              <div class="student-name">
                {{ student.data.first_name }} {{ student.data.last_name_1 }} {{ student.data.last_name_2 }}
              </div>
              <div class="code">{{ student.data.code }}</div>
            </div>
            <div class="student-balance" v-if="app.user.role === 'teacher'" :class="{danger: student.data.config.practice.balance < 0}">
              <div class="form-label">
                {{ app.strings.app.student.profile.practice.balance }}
              </div>
              {{ student.data.config.practice.balance }}
            </div>
          </div>
          <div class="student-contact" v-if="student.data.phone || student.data.email">
            <div v-if="student.data.phone">{{ student.data.phone }}</div>
            <div v-if="student.data.email">{{ student.data.email }}</div>
            <div class="contact" v-if="app.user.role === 'teacher'">
              <a class="call" v-if="student.data.phone" :href="'tel:' + student.data.phone">
                <font-awesome-icon class="icon" :icon="['fas', 'phone-square-alt']" />
                <span>{{ app.strings.app.student.profile.actions.call }}</span>
              </a>
              <a class="whatsapp" v-if="student.data.phone" v-on:click="openWhatsapp(student.data.phone)">
                <font-awesome-icon class="icon" :icon="['fab', 'whatsapp-square']" />
                <span>{{ app.strings.app.student.profile.actions.whatsapp }}</span>
              </a>
              <a class="mail" v-if="student.data.email" :href="'mailto:' + student.data.email">
                <font-awesome-icon class="icon" :icon="['fas', 'envelope-square']" />
                <span>{{ app.strings.app.student.profile.actions.email }}</span>
              </a>
            </div>
          </div>
        </div>

        <div class="book-banner" v-if="canBook && app.studentBookingNotices > 0" v-on:click="open('book')">
          <font-awesome-icon :icon="['far', 'calendar-check']" />
          <span>{{ app.strings.app.student.profile.summary.book_notices }}</span>
          <font-awesome-icon :icon="['fas', 'chevron-right']" />
        </div>

        <div class="profile-main" :class="{'with-action': app.user.role === 'teacher'}">
          <!-- On desktop a student already has these screens in the side menu,
               so the menu is only shown on mobile (and always to teachers). -->
          <menu-vertical
            class="profile-menu"
            :class="{'mobile-only': app.user.role === 'student'}"
            :app="app"
            :color="'blue'"
            :bold="true"
            :padding="{
              top: 0,
              sides: 0
            }"
            :options="options"
          />

          <div class="profile-summary" :class="{'without-balance': app.user.role === 'teacher'}">
            <div class="summary-card summary-upcoming">
              <div class="summary-title">{{ app.strings.app.student.profile.summary.upcoming }}</div>
              <template v-if="summary.loading">
                <loader-dot :size="'12px'" :color_1="'rgb(32, 113, 183)'" :color_2="'rgb(32, 113, 183)'" />
              </template>
              <template v-else>
                <div class="upcoming-item" v-for="practice in summary.upcoming" :key="practice.id" v-on:click="openPractice(practice)">
                  <div class="upcoming-date">
                    <span>{{ practice.when | weekdayShort }}</span>
                    <b>{{ practice.when.getDate() }}</b>
                    <span>{{ practice.when | monthShort }}</span>
                  </div>
                  <div class="upcoming-data">
                    <div class="upcoming-time">{{ clock(practice.when) }} – {{ clock(practice.end) }}</div>
                    <div class="upcoming-meta">{{ practice.type_name || practice.license }}<template v-if="practice.teacher_name"> · {{ app.strings.app.student.profile.summary.with }} {{ practice.teacher_name }}</template></div>
                    <div class="upcoming-meeting" v-if="practice.meeting_point === 'other'" :title="practice.meeting_address">
                      <font-awesome-icon :icon="['fas', 'map-marker-alt']" /> {{ app.strings.app.student.profile.summary.pickup }} {{ practice.meeting_address }}
                    </div>
                  </div>
                  <font-awesome-icon class="upcoming-chevron" :icon="['fas', 'chevron-right']" />
                </div>
                <div class="summary-empty" v-if="summary.upcoming.length === 0">
                  <font-awesome-icon :icon="['far', 'calendar-alt']" />
                  <span>{{ app.strings.app.student.profile.summary.upcoming_empty }}</span>
                </div>
                <a class="summary-link" v-if="app.user.role === 'student'" v-on:click="open('calendar')">{{ app.strings.app.student.profile.summary.see_calendar }} <font-awesome-icon :icon="['fas', 'chevron-right']" /></a>
                <a class="summary-link" v-if="canBook" v-on:click="open('book')">{{ app.strings.app.student.practice.book.menu }} <font-awesome-icon :icon="['fas', 'chevron-right']" /></a>
              </template>
            </div>

            <div class="summary-card summary-grade">
              <div class="summary-title">{{ app.strings.app.student.profile.summary.last_grade }}</div>
              <template v-if="!summary.loading">
                <template v-if="summary.lastGraded">
                  <div class="grade-chip" :class="'grade-' + summary.lastGraded.grade">{{ app.strings.practice.start.grades[summary.lastGraded.grade] }}</div>
                  <div class="grade-meta">{{ summary.lastGraded.when | longDate }}<template v-if="summary.lastGraded.teacher_name"> · {{ summary.lastGraded.teacher_name }}</template></div>
                </template>
                <div class="summary-empty" v-else>
                  <font-awesome-icon :icon="['fas', 'chart-pie']" />
                  <span>{{ app.strings.app.student.profile.summary.last_grade_empty }}</span>
                </div>
              </template>
            </div>

            <div class="summary-card summary-balance" v-if="app.user.role === 'student'">
              <div class="summary-title">{{ app.strings.app.student.profile.summary.balance }}</div>
              <template v-if="!summary.loading">
                <div class="balance-row" v-for="item in summary.bag" :key="item.practice_type.id" :class="{danger: item.balance < 0}">
                  <span>{{ item.practice_type.name }}</span>
                  <b>{{ item.balance }}</b>
                </div>
                <div class="summary-empty" v-if="summary.bag.length === 0">
                  <font-awesome-icon :icon="['fas', 'shopping-bag']" />
                  <span>{{ app.strings.app.student.profile.summary.balance_empty }}</span>
                </div>
                <!-- Only when the school sells classes by card (Miguel, «pruebas de agenda», 7). -->
                <a class="summary-link" v-if="summary.canBuy" v-on:click="open('practice_bag')">{{ app.strings.app.student.profile.summary.buy }} <font-awesome-icon :icon="['fas', 'chevron-right']" /></a>
              </template>
            </div>
          </div>
        </div>
      </div>
    </transition>
    <transition name="fade">
      <div id="student-profile-content" class="max-width-800 margin-auto" v-if="show && loading">
        <loader-dot :size="'16px'" :color_1="'rgb(32, 113, 183)'" :color_2="'rgb(32, 113, 183)'" />
      </div>
    </transition>
    <div class="bottom-action" v-if="app.user.role === 'teacher' && show && !loading && student.data.config">
      <button class="h-button w-100" v-on:click="open('new_practice')">
        {{ app.strings.app.student.profile.actions.new_practice }}
      </button>
    </div>
    <div class="student-practice-pending" v-show="app.user.role === 'student' && this.practice.pending.length > 0">
      <div class="student-practice-pending-form">
        <div class="student-practice-pending-title">
          <font-awesome-icon :icon="['fas', 'pencil-alt']" />
          {{ app.strings.app.student.profile.practice.pending.title }}
          <span class="pending-counter" v-if="practice.total > 1">{{ pendingCounter }}</span>
        </div>
        <div class="student-practice-pending-data" v-if="this.practice.pending.length > 0">
          <div>
            <span>{{ app.strings.app.student.profile.practice.pending.date }}</span>
            <b>{{ this.practice.pending[0].date | humanDate }}</b>
          </div>
          <div>
            <span>{{ app.strings.app.student.profile.practice.pending.time }}</span>
            <b>{{ this.practice.pending[0].time_real }} min</b>
          </div>
        </div>
        <div class="student-practice-pending-signature">
          <signature-box ref="signaturePad" :app="app" :label="app.strings.app.student.profile.practice.pending.signature" :error="signature.error" />
          <button class="h-button w-100 default-2" v-on:click="sign()" v-if="signature.loading === false">
            {{ app.strings.app.student.profile.practice.pending.sign }}
          </button>
          <div v-else>
            <loader-dot :size="'16px'" :color_1="'rgb(32, 113, 183)'" :color_2="'rgb(32, 113, 183)'" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'app-student-profile-view',
  data () {
    return {
      show: false,
      student: {
        loading: false,
        data: {
          id: 0
        }
      },
      practice: {
        pending: [],
        total: 0
      },
      hasSideMenu: false,
      summary: {
        loading: true,
        upcoming: [],
        lastGraded: null,
        bag: [],
        canBuy: false
      },
      signature: {
        loading: false,
        data: '',
        error: false
      }
    }
  },
  props: [
    'app'
  ],
  filters: {
    weekdayShort (date) {
      return ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'][date.getDay()]
    },
    monthShort (date) {
      return ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'][date.getMonth()]
    },
    longDate (date) {
      let months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']
      return date.getDate() + ' de ' + months[date.getMonth()] + ' de ' + date.getFullYear()
    }
  },
  computed: {
    loading () {
      if (this.student.loading) {
        return true
      }

      return false
    },
    // The school lets the student book from the app (direct or request).
    canBook () {
      return this.app.user.role === 'student' && !!this.app.studentBooking && this.app.studentBooking !== 'off'
    },
    // On a student's own profile the arrow leaves for Facilauto, which the
    // side menu already offers on desktop.
    backAction () {
      return this.app.user.role === 'student' && this.hasSideMenu ? null : this.back
    },
    pendingCounter () {
      return this.app.strings.app.student.profile.practice.pending.counter
        .replace('{n}', this.practice.total - this.practice.pending.length + 1)
        .replace('{t}', this.practice.total)
    },
    options () {
      let student = this.app.user.role === 'student'
      let menu = this.app.strings.app.student.profile.menu
      let options = [
        {
          type: 'button',
          icon: ['far', 'calendar-alt'],
          label: student ? menu.my_calendar : menu.calendar,
          action: () => {
            this.open('calendar')
          }
        },
        {
          type: 'button',
          icon: ['fas', 'chart-pie'],
          label: student ? menu.my_evolution : menu.evolution_practice,
          action: () => {
            this.open('evolution')
          }
        }
      ]

      // The student too: his report goes out with his assigned teacher.
      options.push({
        type: 'button',
        icon: ['far', 'file'],
        label: menu.dgt_file,
        action: () => {
          this.open('reports')
        }
      })

      if (this.app.user.role === 'student') {
        if (this.canBook) {
          options.unshift({
            type: 'button',
            icon: ['far', 'calendar-plus'],
            label: this.app.strings.app.student.practice.book.menu,
            action: () => {
              this.open('book')
            }
          })
        }
        options.push({
          type: 'button',
          icon: ['fas', 'shopping-bag'],
          label: this.app.strings.app.student.profile.menu.practice_bag,
          action: () => {
            this.open('practice_bag')
          }
        })
        options.push({
          type: 'button',
          icon: ['fas', 'question-circle'],
          label: this.app.strings.app.student.profile.menu.help,
          action: () => {
            this.open('help')
          }
        })
      }

      return options
    }
  },
  mounted () {
    this.show = true
    this.loadStudentBooking(this.app)

    this.sideMenuMedia = window.matchMedia('(min-width: 1024px)')
    this.onSideMenuMedia = () => {
      this.hasSideMenu = this.sideMenuMedia.matches
    }
    this.sideMenuMedia.addListener(this.onSideMenuMedia)
    this.onSideMenuMedia()

    // A student always sees his own profile; /app/student/profile has no
    // code in the URL, so it comes from the session user, which may still be
    // loading when the view mounts.
    const code = () => (this.app.user.role === 'student' || !this.$route.params.code) ? this.app.user.code : this.$route.params.code
    if (code()) {
      this.student.data.code = code()
      this.studentRead()
    } else {
      const unwatch = this.$watch(() => this.app.user.code, (value) => {
        if (value) {
          unwatch()
          this.student.data.code = code()
          this.studentRead()
        }
      })
    }
  },
  beforeDestroy () {
    this.sideMenuMedia.removeListener(this.onSideMenuMedia)
  },
  methods: {
    back () {
      if (this.app.user.role === 'teacher') {
        this.$router.push({name: 'app.student.list', query: {origin: 'app', navigationLeft: 'false', token: this.app.token}})
      } else if (this.app.user.role === 'student') {
        this.app.methods.back()
      }
    },
    studentRead () {
      if (this.student.loading) {
        return
      }

      this.student.loading = true

      this.$http.get(
        this.app.url.api + this.app.endpoints.student.read.one + this.student.data.code,
        {
          headers: {
            Authorization: 'Token ' + this.app.token
          }
        }
      ).then(response => {
        if (response.status === 200) {
          if (response.body.success === true) {
            this.student.data = response.body.data.student

            let pending = response.body.data.practice.pending
            for (let i in pending) {
              pending[i].date = new Date(pending[i].date)
              pending[i].date.setHours(parseInt(pending[i].hour), parseInt(pending[i].minute), 0, 0)
            }
            this.practice.pending = pending
            this.practice.total = pending.length
            this.summaryRead()
            // The view may be gone (user switched) before the timeout fires.
            setTimeout(() => {
              if (this.$refs.signaturePad) {
                this.$refs.signaturePad.resizeCanvas()
              }
            }, 200)
          } else {
            if (response.body.error.code === 'S100') {
              this.app.methods.logout()
            } else {
              this.showRequestError(this.app.strings, 'student-search', response.body.error)
            }
          }
        } else {
          this.showError(this.app.strings, 'request', 'status-' + response.status)
        }
        this.student.loading = false
      }, () => {
        this.student.loading = false
        this.showError(this.app.strings, 'request', 'status-network: Connection unreachable')
      })
    },
    clock (date) {
      return (date.getHours() < 10 ? '0' : '') + date.getHours() + ':' + (date.getMinutes() < 10 ? '0' : '') + date.getMinutes()
    },
    // Next classes, last grade and balance shown beside the student data.
    summaryRead () {
      let day = (offset) => {
        let d = new Date()
        d.setDate(d.getDate() + offset)
        return d.getFullYear() + '-' + (d.getMonth() + 1 < 10 ? '0' : '') + (d.getMonth() + 1) + '-' + (d.getDate() < 10 ? '0' : '') + d.getDate()
      }
      let headers = {Authorization: 'Token ' + this.app.token}
      this.summary.loading = true
      let requests = [
        this.$http.post(
          this.app.url.api + this.app.endpoints.practice.filter,
          {start_date: day(-365), end_date: day(120), student: this.student.data.code},
          {emulateJSON: true, headers: headers}
        ).then(response => {
          if (response.status !== 200 || response.body.success !== true) {
            return
          }
          let now = new Date()
          let practices = response.body.data.practices.map((p) => {
            let when = new Date(p.date)
            when = new Date(when.getUTCFullYear(), when.getUTCMonth(), when.getUTCDate(), parseInt(p.hour), parseInt(p.minute), 0, 0)
            return Object.assign({}, p, {when: when, end: new Date(when.getTime() + parseInt(p.time) * 60000)})
          })
          this.summary.upcoming = practices
            .filter((p) => p.cancelled !== 1 && p.ended !== 1 && p.end > now)
            .sort((a, b) => a.when - b.when)
            .slice(0, 4)
          let graded = practices
            .filter((p) => p.ended === 1 && p.grade > 0)
            .sort((a, b) => b.when - a.when)
          this.summary.lastGraded = graded.length > 0 ? graded[0] : null
        }, () => {})
      ]
      if (this.app.user.role === 'student') {
        requests.push(this.$http.get(
          this.app.url.api + this.app.endpoints.student.practice.bag.read.all,
          {headers: headers}
        ).then(response => {
          if (response.status === 200 && response.body.success === true) {
            this.summary.bag = response.body.data.bag
            this.summary.canBuy = response.body.data.can_buy === true
          }
        }, () => {}))
      }
      Promise.all(requests).then(() => {
        this.summary.loading = false
      })
    },
    // A teacher goes to that class, and comes back here from it.
    openPractice (practice) {
      if (this.app.user.role !== 'teacher') {
        this.open('calendar')
        return
      }
      this.$router.push({name: 'app.teacher.practice.menu', params: {id: practice.id}, query: {origin: 'app', navigationLeft: 'false', token: this.app.token, student: this.student.data.code}})
    },
    open (target) {
      switch (target) {
        case 'calendar':
          this.$router.push({name: 'app.practice.calendar', query: {origin: 'app', navigationLeft: 'false', token: this.app.token, student: this.student.data.code}})
          break;
        case 'evolution':
          this.$router.push({name: 'app.student.evolution', params: {code: this.student.data.code}, query: {origin: 'app', navigationLeft: 'false', token: this.app.token}})
          break;
        case 'reports':
          this.$router.push({name: 'app.student.dgtreport', params: {code: this.student.data.code}, query: {origin: 'app', navigationLeft: 'false', token: this.app.token}})
          break;
        case 'help':
          this.$router.push({name: 'app.help', query: {origin: 'app', navigationLeft: 'false', token: this.app.token}})
          break;
        case 'practice_bag':
          this.$router.push({name: 'app.student.practice.bag.list', query: {origin: 'app', navigationLeft: 'false', token: this.app.token}})
          break;
        case 'new_practice':
          this.$router.push({name: 'app.teacher.practice.create', query: {origin: 'app', navigationLeft: 'false', token: this.app.token, student: this.student.data.code}})
          break;
        case 'book':
          this.$router.push({name: 'app.student.practice.book', query: {origin: 'app', navigationLeft: 'false', token: this.app.token}})
          break;
      }
    },
    sign () {
      if (this.signature.loading) {
        return
      }

      this.signature.error = false

      const { isEmpty, data } = this.$refs.signaturePad.saveSignature('image/jpeg')
      if (isEmpty) {
        this.signature.error = true
        return
      }

      this.signature.data = data

      this.signature.loading = true

      this.$http.post(
        this.app.url.api + this.app.endpoints.practice.sign,
        {
          id: this.practice.pending[0].id,
          signature: data
        },
        {
          emulateJSON: true,
          headers: {
            Authorization: 'Token ' + this.app.token
          }
        }
      ).then(response => {
        this.signature.loading = false
        if (response.status === 200) {
          if (response.body.success === true) {
            this.$refs.signaturePad.clearSignature()
            this.practice.pending.splice(0, 1)
          } else {
            if (response.body.error.code === 'S100') {
              this.app.methods.logout()
            } else {
              this.showRequestError(this.app.strings, 'practice-sign', response.body.error)
            }
          }
        } else {
          this.showError(this.app.strings, 'request', 'status-' + response.status)
        }
      }, () => {
        this.signature.loading = false
        this.showError(this.app.strings, 'request', 'status-network: Connection unreachable')
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/variables-app.scss";
@import "@/assets/styles/variables-valkiria.scss";

#student-profile-content {
  position: relative;
  width: 100%;
  height: 100%;
  padding-top: 60px;
  box-sizing: border-box;
}

.student-data {
  margin: 12px 0;
  padding: 16px 18px;
  box-sizing: border-box;
  background-color: #FFFFFF;
  border: 1px solid #e8edf3;
  border-radius: 24px;
  box-shadow: 0 12px 30px rgba(20, 67, 107, 0.08);
}

.student-head {
  display: flex;
  align-items: center;
  gap: 12px;
}

.student-head > img {
  flex: 0 0 auto;
  width: 56px;
  height: 56px;
  border-radius: 999px;
  border: 2px solid rgba(32, 113, 183, 0.25);
  background-color: rgba(32, 113, 183, 0.1);
  box-sizing: border-box;
}

.student-identity {
  flex: 1;
  min-width: 0;
}

.student-name {
  color: #1f2a37;
  font-family: 'Lexend', sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.25;
  overflow-wrap: break-word;
}

.student-identity .code {
  margin-top: 2px;
  color: #2071b7;
  font-family: 'Lexend', sans-serif;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.student-balance {
  flex: 0 0 auto;
  padding: 6px 12px;
  border: 1px solid #e8edf3;
  border-radius: 14px;
  background-color: #f5f8fb;
  color: #1f2a37;
  font-family: 'Lexend', sans-serif;
  font-size: 20px;
  font-weight: 600;
  text-align: center;
}

.student-balance.danger {
  border-color: rgba(220, 53, 69, 0.3);
  background-color: rgba(220, 53, 69, 0.12);
  color: #b91c1c;
}

.student-balance .form-label {
  margin: 0 !important;
  color: #64748b !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

// Long names keep their words whole on the narrowest phones.
@media (max-width: 359px) {
  .student-name {
    font-size: 16px;
  }

  .student-balance {
    padding: 6px 8px;
  }
}

.student-contact {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #eef2f7;
  color: #64748b;
  font-size: 14px;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.contact {
  display: grid;
  grid-auto-columns: minmax(0, 1fr);
  grid-auto-flow: column;
  gap: 8px;
  margin-top: 10px;
}

.contact > a {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-height: 56px;
  border-radius: 12px;
  background-color: #f5f8fb;
  color: #1f2a37 !important;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
}

.contact > a > .icon {
  font-size: 22px;
}

.contact > .call > .icon {
  color: #16a34a;
}

.contact > .whatsapp > .icon {
  color: #075e54;
}

.contact > .mail > .icon {
  color: #2071b7;
}

@media (min-width: 768px) {
  #student-profile-content {
    display: grid;
    grid-template-columns: 280px minmax(0, 1fr);
    grid-template-rows: auto 1fr;
    gap: 16px;
    align-items: start;
    padding-top: 72px;
  }

  .student-data {
    grid-row: 1 / span 2;
    position: sticky;
    top: 72px;
    margin: 0;
    padding: 22px 18px;
  }

  .student-head {
    flex-direction: column;
    text-align: center;
  }

  .student-head > img {
    width: 96px;
    height: 96px;
    border-width: 3px;
  }

  .student-identity {
    width: 100%;
  }

  .student-name {
    font-size: 22px;
  }

  .student-balance {
    padding: 8px 18px;
    font-size: 22px;
  }

  .student-contact {
    text-align: center;
  }

  .book-banner {
    grid-column: 2;
    margin: 0;
  }
}

@media (min-width: 1024px) {
  #student-profile-content {
    grid-template-columns: minmax(300px, 380px) minmax(0, 1fr);
    gap: 28px;
    padding-top: 92px !important;
  }

  .student-data {
    top: 92px;
  }
}

.profile-main {
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-width: 0;
}

// Room for the fixed button of the teacher.
.profile-main.with-action {
  padding-bottom: 96px;
}

@media (min-width: 768px) {
  .profile-main {
    grid-column: 2;
  }

  .profile-menu ::v-deep .menu .menu-item:first-child {
    margin-top: 0 !important;
  }
}

.profile-summary {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 14px;
}

.summary-card {
  min-width: 0;
  padding: 16px 18px;
  border: 1px solid #e8edf3;
  border-radius: 20px;
  background-color: #FFFFFF;
  box-shadow: 0 12px 30px rgba(20, 67, 107, 0.06);
}

.summary-title {
  margin-bottom: 10px;
  color: #64748b;
  font-family: 'Lexend', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.upcoming-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 0;
  border-top: 1px solid #eef2f7;
  cursor: pointer;
}

.upcoming-item:first-of-type {
  border-top: none;
  padding-top: 2px;
}

.upcoming-date {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: center;
  width: 54px;
  padding: 6px 0;
  border-radius: 12px;
  background-color: rgba(32, 113, 183, 0.1);
  color: #2071b7;
  font-size: 11px;
  line-height: 1.2;
  text-transform: uppercase;
}

.upcoming-date > b {
  font-family: 'Lexend', sans-serif;
  font-size: 20px;
}

.upcoming-data {
  flex: 1;
  min-width: 0;
}

.upcoming-chevron {
  flex: 0 0 auto;
  color: #94a3b8;
  font-size: 12px;
}

.upcoming-time {
  color: #1f2a37;
  font-family: 'Lexend', sans-serif;
  font-weight: 600;
}

.upcoming-meta,
.upcoming-meeting {
  overflow: hidden;
  color: #64748b;
  font-size: 13px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.upcoming-meeting {
  color: #2071b7;
}

.summary-empty {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 14px;
  background-color: #f5f8fb;
  color: #64748b;
  font-size: 14px;
}

.summary-empty > svg {
  flex: 0 0 auto;
  color: #94a3b8;
  font-size: 20px;
}

.summary-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  color: #2071b7 !important;
  font-family: 'Lexend', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.summary-link {
  margin-right: 18px;
}

.summary-link > svg {
  font-size: 11px;
}

.grade-chip {
  display: inline-block;
  padding: 6px 14px;
  border-radius: 999px;
  font-family: 'Lexend', sans-serif;
  font-size: 18px;
  font-weight: 700;
}

.grade-chip.grade-1 { background-color: rgba(220, 53, 69, 0.12); color: #b91c1c; }
.grade-chip.grade-2 { background-color: #fff4e6; color: #d97706; }
.grade-chip.grade-3 { background-color: rgba(32, 113, 183, 0.1); color: #2071b7; }
.grade-chip.grade-4 { background-color: rgba(22, 163, 74, 0.12); color: #0f766e; }

.grade-meta {
  margin-top: 8px;
  color: #64748b;
  font-size: 13px;
}

.balance-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-top: 1px solid #eef2f7;
  color: #1f2a37;
  font-size: 14px;
}

.balance-row:first-of-type {
  border-top: none;
  padding-top: 0;
}

.balance-row > b {
  min-width: 36px;
  padding: 4px 10px;
  border-radius: 10px;
  background-color: rgba(32, 113, 183, 0.1);
  color: #2071b7;
  font-family: 'Lexend', sans-serif;
  text-align: center;
}

.balance-row.danger > b {
  background-color: rgba(220, 53, 69, 0.12);
  color: #b91c1c;
}

@media (min-width: 1024px) {
  .profile-menu.mobile-only {
    display: none;
  }

  .profile-summary {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }

  .summary-upcoming {
    grid-row: span 2;
  }

  .without-balance .summary-upcoming {
    grid-row: auto;
  }

  // The three shortcuts of a teacher, in one row.
  .profile-menu ::v-deep .menu {
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
  }

  .profile-menu ::v-deep .menu .menu-item {
    flex-direction: column;
    justify-content: center;
    gap: 8px;
    padding: 14px 8px;
    text-align: center;
  }

  .profile-menu ::v-deep .menu .menu-item > .icon {
    margin-right: 0;
  }

  .profile-menu ::v-deep .menu .menu-item > span {
    flex: none;
  }
}

.student-practice-pending {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  background: rgba(15, 23, 42, 0.56);
  backdrop-filter: blur(4px);
  align-items: center;
  justify-content: center;
  padding: 12px;
  box-sizing: border-box;
  flex-direction: column;
  z-index: 2400;
}

.student-practice-pending-form {
  width: 500px;
  max-width: 100%;
  height: auto;
  max-height: 100%;
  padding: 14px;
  box-sizing: border-box;
  background-color: #FFFFFF;
  border-radius: 20px;
  margin: 6px 0;
  box-shadow: 0 22px 44px rgba(2, 6, 23, 0.3);
}

.student-practice-pending-title {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 4px 12px;
  border-bottom: 1px solid #eef2f7;
  color: #1f2a37;
  font-family: 'Lexend', sans-serif;
  font-size: 18px;
  font-weight: 700;
}

.student-practice-pending-title > svg {
  color: #2071b7;
}

.pending-counter {
  flex: 0 0 auto;
  margin-left: auto;
  padding: 3px 10px;
  border-radius: 999px;
  background-color: #eaf3fb;
  color: #1a5d98;
  font-size: 13px;
  font-weight: 600;
}

.student-practice-pending-data {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 12px 0 0;
}

.student-practice-pending-data > div {
  padding: 10px 12px;
  border-radius: 12px;
  background-color: #f5f8fb;
}

.student-practice-pending-data span {
  display: block;
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.student-practice-pending-data b {
  display: block;
  margin-top: 3px;
  color: #1f2a37;
  font-family: 'Lexend', sans-serif;
  font-size: 15px;
}

.student-practice-pending-signature .h-button {
  margin-top: 14px;
}


.book-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 14px;
  padding: 12px 14px;
  border-radius: 16px;
  background-color: #eaf3fb;
  color: #1a5d98;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.book-banner > span {
  flex: 1;
}
</style>
